import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TwoFactorAuthenticationMethod } from '@shared/models';
import { ITwoFactorVerificationService, TWO_FACTOR_VERIFICATION_SERVICE, VerificationFlowResult } from '@shared/models/two-factor-verification';
import { interval, map } from 'rxjs';

@Component({
  selector: 'lib-email-verification-modal',
  templateUrl: 'two-factor-verification-modal.component.html',
  imports: [FormsModule, ReactiveFormsModule],
})
export class TwoFactorVerificationModalComponent implements OnInit {
  public readonly activeModal = inject(NgbActiveModal);
  public readonly twoFactorVerificationService = inject<ITwoFactorVerificationService>(TWO_FACTOR_VERIFICATION_SERVICE);

  private readonly nowSignal = toSignal(interval(1000).pipe(map(() => Date.now())), { initialValue: Date.now() });

  public verificationCode = new FormControl<string>('', {
    validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
  });

  public rememberMe = false;

  public chosenMethod = signal<TwoFactorAuthenticationMethod | undefined>(undefined);

  public codeFailedValidation = false;

  // Whether or not the user needs to request a new token
  protected readonly requiresNewVerificationCode = computed(() => {
    const method = this.chosenMethod();
    if (!method) {
      return true;
    }

    if (this.twoFactorVerificationService.requiresNewVerificationCode(method)) {
      return true;
    }

    const verificationCodeExpiresAt = this.twoFactorVerificationService.verificationCodeExpiresAt(method);
    return verificationCodeExpiresAt !== null && verificationCodeExpiresAt.getTime() < this.nowSignal();
  });

  // How long the user must wait before being able to request a new verification code
  protected readonly canRequestVerificationCodeIn = computed(() => {
    const method = this.chosenMethod();
    if (!method) {
      return 0;
    }

    const canRequestAt = this.twoFactorVerificationService.canRequestVerificationCodeAt(method);
    return canRequestAt ? Math.round((canRequestAt.getTime() - this.nowSignal()) / 1000) : 0;
  });

  // How long the user must wait before being able to make another verification attempt
  protected readonly canAttemptVerificationIn = computed(() => {
    const method = this.chosenMethod();
    if (!method) {
      return 0;
    }

    const canAttemptAt = this.twoFactorVerificationService.canAttemptVerificationAt(method);
    return canAttemptAt ? Math.round((canAttemptAt.getTime() - this.nowSignal()) / 1000) : 0;
  });

  protected readonly isEmailBased = computed(() => this.chosenMethod() === TwoFactorAuthenticationMethod.Email);
  protected readonly isAppBased = computed(() => this.chosenMethod() === TwoFactorAuthenticationMethod.App);

  public constructor() {
    // Disable the verification input if the user requires a new code or must wait before another attempt
    effect(() => {
      if (this.requiresNewVerificationCode() || this.canAttemptVerificationIn() > 0) {
        this.verificationCode.disable();
      } else {
        this.verificationCode.enable();
      }
    });
  }

  public ngOnInit(): void {
    this.requestVerification();
  }

  public get isCodeInvalid() {
    return this.codeFailedValidation || (this.verificationCode.touched && this.verificationCode.invalid);
  }

  public verify(): void {
    this.verificationCode.markAsTouched();
    if (!this.verificationCode.valid) {
      return;
    }

    const method = this.chosenMethod();
    if (!method) {
      return;
    }

    this.codeFailedValidation = false;
    this.twoFactorVerificationService.submitVerificationCode(this.verificationCode.value ?? '', method).subscribe({
      next: () => this.completeVerification(),
      error: () => {
        this.verificationCode.setValue('');
        this.verificationCode.markAsUntouched();
        this.codeFailedValidation = true;
      },
    });
  }

  public requestVerification(): void {
    this.twoFactorVerificationService.requestVerificationCode(this.chosenMethod()).subscribe(res => {
      this.chosenMethod.set(res.method);
      this.codeFailedValidation = false;
    });
  }

  public useEmail() {
    this.chosenMethod.set(TwoFactorAuthenticationMethod.Email);
    this.requestVerification();
  }

  public completeVerification(): void {
    this.activeModal.close({
      shouldSave: this.rememberMe,
    } as VerificationFlowResult);
  }
}
